import { addWeeks, compareDates } from '@/domain/dates';
import type { Assignment, DayType, IsoDate, Outcome, Volunteer } from '@/shared/types';

/**
 * The credit system (spec 3).
 *
 * Two rules drive every decision here:
 *  - Nothing is ever negative. Penalising a bail would push frequent bailers
 *    *up* a lowest-credit-first list, which is backwards.
 *  - Only the rolling window counts, so a burst of rides six months ago does
 *    not deprioritise someone forever.
 */

/** Credit actually earned by a settled outcome. */
export function creditForOutcome(outcome: Outcome): number {
  switch (outcome) {
    case 'Completed':
      return 1;
    case 'Rider Canceled':
    case 'Volunteer Bailed':
    case 'Pending':
    case 'No Meeting':
      return 0;
  }
}

/**
 * The load an assignment represents for ranking purposes.
 *
 * A settled row contributes its earned credit. A row that is still `Pending`
 * but has a volunteer on it contributes 1, because we expect it to happen —
 * without this, generating three months in one pass would hand every single
 * date to whoever currently has the lowest credit.
 */
export function loadOfAssignment(assignment: Assignment): number {
  if (assignment.outcome === 'No Meeting') return 0;
  if (assignment.outcome === 'Pending') return assignment.volunteerId ? 1 : 0;
  return creditForOutcome(assignment.outcome);
}

export type PauseState = 'active' | 'paused' | 'removed';

/** Whether a volunteer is available on a given date, and why not if not. */
export function pauseStateOn(volunteer: Volunteer, date: IsoDate): PauseState {
  if (volunteer.status === 'Removed') return 'removed';

  // A `Paused until` in the future pauses them regardless of the Status cell,
  // and a date in the past un-pauses them without the coordinator having to
  // remember to flip Status back.
  const pausedByDate =
    volunteer.pausedUntil !== null && compareDates(date, volunteer.pausedUntil) <= 0;

  if (volunteer.status === 'Paused') {
    return volunteer.pausedUntil === null || pausedByDate ? 'paused' : 'active';
  }
  return pausedByDate ? 'paused' : 'active';
}

export function isAvailableOnDay(volunteer: Volunteer, dayType: DayType): boolean {
  return dayType === 'Thursday' ? volunteer.availableThursday : volunteer.availableSunday;
}

export function isEligible(volunteer: Volunteer, date: IsoDate, dayType: DayType): boolean {
  return pauseStateOn(volunteer, date) === 'active' && isAvailableOnDay(volunteer, dayType);
}

interface Entry {
  date: IsoDate;
  load: number;
  /** Settled or planned; only used for "last assigned" bookkeeping. */
  counted: boolean;
}

/**
 * An append-only tally of per-volunteer load by date.
 *
 * Generation mutates this as it goes (`record`), so each successive date in a
 * batch sees the assignments the batch has already made. That is what turns a
 * one-shot generate into a rotation instead of the same name 26 times.
 */
export class CreditLedger {
  private readonly entries = new Map<string, Entry[]>();

  constructor(
    private readonly volunteers: Map<string, Volunteer>,
    private readonly rollingWindowWeeks: number,
  ) {}

  static from(
    volunteers: readonly Volunteer[],
    assignments: readonly Assignment[],
    rollingWindowWeeks: number,
  ): CreditLedger {
    const ledger = new CreditLedger(new Map(volunteers.map((v) => [v.id, v])), rollingWindowWeeks);
    for (const assignment of assignments) {
      if (!assignment.volunteerId) continue;
      ledger.record(assignment.volunteerId, assignment.date, loadOfAssignment(assignment));
    }
    return ledger;
  }

  /** Deep copy, so a preview can be computed without touching live state. */
  clone(): CreditLedger {
    const copy = new CreditLedger(this.volunteers, this.rollingWindowWeeks);
    for (const [id, entries] of this.entries) copy.entries.set(id, entries.slice());
    return copy;
  }

  record(volunteerId: string, date: IsoDate, load: number): void {
    const list = this.entries.get(volunteerId);
    const entry: Entry = { date, load, counted: true };
    if (list) list.push(entry);
    else this.entries.set(volunteerId, [entry]);
  }

  /** Undo a previously recorded entry, used when a generation reassigns a row. */
  unrecord(volunteerId: string, date: IsoDate): void {
    const list = this.entries.get(volunteerId);
    if (!list) return;
    const index = list.findIndex((e) => e.date === date);
    if (index >= 0) list.splice(index, 1);
  }

  /**
   * Effective credit for a volunteer as of `asOf`, counting the window
   * `[asOf - rollingWindowWeeks, asOf)`.
   *
   * The window is evaluated relative to the date being *scheduled*, not to
   * today. When generating three months out, that is what lets load from
   * early in the batch roll off before the end of the batch.
   */
  creditAt(volunteerId: string, asOf: IsoDate): number {
    const windowStart = addWeeks(asOf, -this.rollingWindowWeeks);
    let total = 0;

    for (const entry of this.entries.get(volunteerId) ?? []) {
      if (compareDates(entry.date, windowStart) >= 0 && compareDates(entry.date, asOf) < 0) {
        total += entry.load;
      }
    }

    // The seed only counts while the seeding event itself is inside the
    // window. That is what makes it "age out naturally" (spec 3.3) rather
    // than sitting on the volunteer's score forever.
    const volunteer = this.volunteers.get(volunteerId);
    if (volunteer && volunteer.seedCredit !== 0) {
      if (volunteer.seededOn === null || compareDates(volunteer.seededOn, windowStart) >= 0) {
        total += volunteer.seedCredit;
      }
    }

    return total;
  }

  /** Most recent date this volunteer was on the hook, strictly before `asOf`. */
  lastAssignedBefore(volunteerId: string, asOf: IsoDate): IsoDate | null {
    let latest: IsoDate | null = null;
    for (const entry of this.entries.get(volunteerId) ?? []) {
      if (!entry.counted) continue;
      if (compareDates(entry.date, asOf) >= 0) continue;
      if (latest === null || compareDates(entry.date, latest) > 0) latest = entry.date;
    }
    return latest;
  }
}

/**
 * Deterministic tie-break, standing in for the spec's "then random".
 *
 * Deterministic matters: pressing Generate twice on unchanged data must
 * produce the same schedule, or the preview you approved is not the schedule
 * you get. Seeding by volunteer *and* date keeps the ordering shuffled across
 * dates rather than fixed for the whole batch.
 */
export function tieBreakHash(volunteerId: string, date: IsoDate): number {
  let hash = 0x811c9dc5;
  const input = `${volunteerId}@${date}`;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash;
}

export interface Candidate {
  volunteer: Volunteer;
  credit: number;
  lastAssigned: IsoDate | null;
}

/**
 * Rank eligible volunteers for a date, lowest effective credit first
 * (spec 3.2). Ties break to least-recently-assigned, then to the stable hash.
 */
export function rankCandidates(
  volunteers: readonly Volunteer[],
  ledger: CreditLedger,
  date: IsoDate,
  dayType: DayType,
): Candidate[] {
  const candidates: Candidate[] = [];

  for (const volunteer of volunteers) {
    if (!isEligible(volunteer, date, dayType)) continue;
    candidates.push({
      volunteer,
      credit: ledger.creditAt(volunteer.id, date),
      lastAssigned: ledger.lastAssignedBefore(volunteer.id, date),
    });
  }

  candidates.sort((a, b) => {
    if (a.credit !== b.credit) return a.credit - b.credit;

    // Never assigned in the window sorts first.
    if (a.lastAssigned !== b.lastAssigned) {
      if (a.lastAssigned === null) return -1;
      if (b.lastAssigned === null) return 1;
      const byDate = compareDates(a.lastAssigned, b.lastAssigned);
      if (byDate !== 0) return byDate;
    }

    return tieBreakHash(a.volunteer.id, date) - tieBreakHash(b.volunteer.id, date);
  });

  return candidates;
}

/**
 * The credit a volunteer joining today should start with: the current average
 * across active volunteers (spec 3.3). Dropping them in at the average puts
 * them mid-rotation instead of at the top of every list for a month.
 */
export function suggestSeedCredit(
  volunteers: readonly Volunteer[],
  ledger: CreditLedger,
  asOf: IsoDate,
  excludeVolunteerId?: string,
): number {
  const active = volunteers.filter(
    (v) => v.id !== excludeVolunteerId && pauseStateOn(v, asOf) === 'active',
  );
  if (active.length === 0) return 0;

  const total = active.reduce((sum, v) => sum + ledger.creditAt(v.id, asOf), 0);
  return Math.round((total / active.length) * 100) / 100;
}
